"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var either = require("./either");
var fn_1 = require("./fn");
// Turn a failure into a success and a success into a failure.
function expectFailure(p) {
    return new Promise(function (res, rej) {
        p.then(rej).catch(res);
    });
}
exports.expectFailure = expectFailure;
// A little less alarming alias for when we are actually
// trying to capture the catch value.
exports.flipP = expectFailure;
// Assert the `predicate` holds for the value in the
// success path of `promise`
exports.assert = function (pro) { return function (pre) {
    return pro.then(function (t) {
        if (pre(t)) {
            return Promise.resolve(t);
        }
        else {
            return Promise.reject(t);
        }
    });
}; };
// Compose two promise producing functions together
// compose(v => Promise.resolve(v*2))(v2 => Promise.resolve(v2*2))(1)
// .then(console.log)
// prints 4
exports.compose = function (f1) { return function (f2) { return function (v) { return f1(v).then(f2); }; }; };
// Try f1, if it fails, try f2.
function orElse(f1, f2) {
    return function (a) { return f1(a).catch(function (e1) { return f2(a).catch(function (e2) { return Promise.reject([e1, e2]); }); }); };
}
exports.orElse = orElse;
// Return a function that will exit with a message when called
// promise.catch(abort("there's something strange going on."));
exports.abort = function (msg) { return function () {
    if (msg) {
        console.log(msg);
    }
    // process.exit(1);
}; };
// Delay by ms milliseconds, then return v as the successful
// result of a promise.
// e.g. delay(2000)(1)
// getApiAccount(accountId).then(delay(2000))
exports.delay = function (ms) { return function (v) {
    return new Promise(function (res, _) {
        // A small optimisation to avoid going back into
        // the event loop queue
        if (ms <= 0) {
            return res(v);
        }
        setTimeout(function () { return res(v); }, ms);
    });
}; };
// Delay until a predicate returns true, checking every interval ms.
exports.delayUntil = function (p, interval) { return function (v) {
    return new Promise(function (resolve, reject) {
        function wait() {
            if (p()) {
                return resolve(v);
            }
            setTimeout(wait, interval);
        }
        wait();
    });
}; };
// Keep the input in a pair along with the output.
exports.stash = function (op) { return function (input) {
    return op(input).then(function (output) { return fn_1.pair(input, output); });
}; };
// Always execute the (presumably side-effecting) function fn regardless of
// whether pa succeeds or fails. If pa fails the error is propagated, if fn
// also fails both errors are accumulated.
// This has some catastrophic performance implications if you create 1 million
// failing promises.
function always(pa, fn) {
    return pa
        .catch(function (e1) {
        return try_(function () { return fn(); })
            .catch(function (e2) { return Promise.reject([e2, e1]); })
            .then(function (_) { return Promise.reject(e1); });
    })
        .then(function (a) { return fn().then(function (_) { return a; }); });
}
exports.always = always;
// Run an operation `count` number of times, resolving a promise when it's done
// Warning: Not stack safe. `count` should be kept to less than a couple of million.
function repeatP(fp, loopDelay, count) {
    if (count > 0) {
        return always(try_(fp), function () { return repeatP(fp, loopDelay, --count); }).catch(function (_) { return _; });
    }
    return Promise.resolve();
}
exports.repeatP = repeatP;
// Run an operation `count` number of times, ignoring the result, returning immediately.
function repeat(fp, loopDelay, count) {
    // So NaN results in true
    if (!(count <= 0)) {
        always(try_(fp), function () { return Promise.resolve(repeat(fp, loopDelay, --count)); }).catch(function (_) { return undefined; });
    }
}
exports.repeat = repeat;
// Run a promise producing function indefinitely without blowing our heap.
function forever(fp, loopDelay) {
    repeat(fp, loopDelay, NaN);
}
exports.forever = forever;
// If p takes longer than ms to resolve, fail with the output of e
function timeout(p, ms, e) {
    var timer;
    return Promise.race([
        p.then(function (result) {
            clearTimeout(timer);
            return result;
        }),
        new Promise(function (res, rej) {
            timer = setTimeout(function () {
                rej(e());
            }, ms);
        }),
    ]);
}
exports.timeout = timeout;
exports.liftP0 = function (fn, that) { return function () {
    return new Promise(function (res, rej) {
        fn.apply(that || fn, [
            function (e, v) {
                if (e) {
                    return rej(e);
                }
                return res(v);
            },
        ]);
    });
}; };
exports.liftP1 = function (fn, that) { return function (a) {
    return new Promise(function (res, rej) {
        fn.apply(that || fn, [
            a,
            function (e, v) {
                if (e) {
                    return rej(e);
                }
                return res(v);
            },
        ]);
    });
}; };
exports.liftP2 = function (fn, that) { return function (a, b) {
    return new Promise(function (res, rej) {
        fn.apply(that || fn, [
            a,
            b,
            function (e, v) {
                if (e) {
                    return rej(e);
                }
                return res(v);
            },
        ]);
    });
}; };
exports.liftP3 = function (fn, that) { return function (a, b, c) {
    return new Promise(function (res, rej) {
        fn.apply(that || fn, [
            a,
            b,
            c,
            function (e, v) {
                if (e) {
                    return rej(e);
                }
                return res(v);
            },
        ]);
    });
}; };
exports.liftP4 = function (fn, that) { return function (a, b, c, d) {
    return new Promise(function (res, rej) {
        fn.apply(that || fn, [
            a,
            b,
            c,
            d,
            function (e, v) {
                if (e) {
                    return rej(e);
                }
                return res(v);
            },
        ]);
    });
}; };
// Fold an array holding promise producing functions.
// Image array.reduce(fn, init), if fn could return a promise.
function foldP(fns, init) {
    if (fns.length <= 0) {
        return Promise.resolve(init);
    }
    return fns[0](init).then(function (carry) { return foldP(fns.slice(1), carry); });
}
exports.foldP = foldP;
// Try an f, if it fails return a rejected promise
// carrying the error.
function try_(f) {
    try {
        return Promise.resolve(f());
    }
    catch (e) {
        return Promise.reject(e);
    }
}
exports.try_ = try_;
function splitN(arr, n) {
    var res = new Array();
    for (var i = 0; i < arr.length; i += n) {
        res.push(arr.slice(i, i + n));
    }
    return res;
}
// Batch up a set of async tasks into a size of n
function batch(fns, n) {
    return splitN(fns, n).map(function (fs) {
        return function () { return Promise.all(fs.map(function (f) { return f(); })); };
    });
}
exports.batch = batch;
// Retry a Promise producing computation `retries` number or times, delayed by `delayMs`, each try.
function retry(fn, retries, delayMs) {
    if (delayMs === void 0) { delayMs = 0; }
    return fn().catch(function (e) {
        console.log(e);
        if (retries > 0) {
            return exports.delay(delayMs)(fn_1.Void()).then(function () { return retry(fn, retries - 1, delayMs); });
        }
        return Promise.reject(e);
    });
}
exports.retry = retry;
// Poll again later
function pending() {
    return either.left(undefined);
}
exports.pending = pending;
// We have a result.
function ready(v) {
    return either.right(v);
}
exports.ready = ready;
// poll allows us to keep trying an operation until it either
// - produces a result
// - produces an error
// - the maximum number of tries is reached.
// It does this without accumulating stack frames, so should be safe for a very large number of retries.
// The trade off is we can't provide the normal zero delay optimization.
function poll(fn, retries, delayMs) {
    if (delayMs === void 0) { delayMs = 0; }
    return new Promise(function (resolve, reject) {
        var tries = 0;
        function doTry() {
            if (tries >= retries) {
                return reject(new Error("Poll failed: maximum number of attempts reached after " + tries + " attempts."));
            }
            tries++;
            fn()
                .then(function (res) {
                res.map(resolve).lmap(function () {
                    // We could do the `if delayMs < 0` optimisation here that would avoid going
                    // through the event loop, but we would end up accumulating stack frames.
                    setTimeout(doTry, delayMs);
                });
            })
                .catch(reject);
        }
        doTry();
    });
}
exports.poll = poll;
// Run all promises without concurrency
function process(fns, idx) {
    var index = typeof idx === 'undefined' ? 0 : idx;
    if (index >= fns.length) {
        return Promise.resolve();
    }
    var f = fns[index];
    return f()
        .then(function (_) { return process(fns, index + 1); })
        .catch(function (e) {
        console.log(e);
        return process(fns, index + 1);
    });
}
exports.process = process;
function all(fns, carry, idx) {
    var index = typeof idx === 'undefined' ? 0 : idx;
    var carried = typeof carry === 'undefined' ? [] : carry;
    if (index >= fns.length) {
        return Promise.resolve(carried);
    }
    var f = fns[index];
    return f()
        .then(function (v) {
        carried.push(v);
        return all(fns, carry, index + 1);
    })
        .catch(function (e) {
        return all(fns, carry, index + 1);
    });
}
exports.all = all;
function splitSuccess(ps) {
    var success = [];
    var failure = [];
    return Promise.all(ps.map(function (p) {
        return p
            .then(function (r) {
            success.push(p);
            return r;
        })
            .catch(function (e) {
            failure.push(p);
            return undefined;
        });
    })).then(function (_) { return fn_1.pair(failure, success); });
}
exports.splitSuccess = splitSuccess;
var Timer = /** @class */ (function () {
    function Timer(name) {
        this.beginning = 0;
        this.end = 0;
        this.sum = 0;
        this.name = name;
    }
    Timer.prototype.start = function () {
        var _this = this;
        return function (a) {
            _this.beginning = Date.now();
            return Promise.resolve(a);
        };
    };
    Timer.prototype.stop = function () {
        var _this = this;
        return function (a) {
            _this.end = Date.now();
            _this.sum += _this.end - _this.beginning;
            return Promise.resolve(a);
        };
    };
    Timer.prototype.resume = function () {
        this.beginning = Date.now();
        this.end = this.beginning;
    };
    Timer.prototype.print = function () {
        var _this = this;
        return function (a) {
            console.log(_this.name + ": " + _this.sum + "ms");
            return Promise.resolve(a);
        };
    };
    return Timer;
}());
exports.Timer = Timer;
function asCallback(f) {
    return function (event, context, callback) {
        try_(function () { return f(event, context); })
            .then(fn_1.id)
            .then(function (res) { return callback(undefined, res); })
            .catch(function (err) { return callback(err, undefined); });
    };
}
exports.asCallback = asCallback;
